import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { CaretDown } from '@phosphor-icons/react/dist/ssr/CaretDown';
import PillInput from '../../../UISystem/Forms/PillInput';
import ContactTypeInput from '../../../UISystem/Forms/ContactTypeInput';
import { ButtonRounded } from '../../../UISystem/Buttons';
import { whitePill } from '../../../UISystem/sharePillStyles';
import { SHADOWS, BUTTON_TOKENS } from '../../../../styles/UISystem';
import { ALL_CONTACT_TYPES } from '../../../../utils/contactTypeConfig';

// Matches ShareSheet's COLLAPSED_COUNT — one collapse rule across the app.
const COLLAPSED_COUNT = 2;

// Same offer rule as TEXT (device pass 1j): the trio is ALWAYS there; two-way
// channels join only when the Profile shared theirs (reciprocity — never ask
// for a channel the host didn't offer). Content-broadcast types never appear:
// this is how people message each other, not a follow.
const ALWAYS_TYPES = ['phone', 'instagram', 'email'];
const IF_SHARED_TYPES = ['whatsapp', 'linkedin', 'telegram', 'signal', 'snapchat'];

/**
 * AnonThreadShareSheet — THREAD's structured share (variants block plan 3,
 * Step 6; designs `anon_4_name-add`, `anon_5_contact-select`,
 * `anon_6_contact-add`).
 *
 * NAME → CHANNEL → VALUE, which is the order the mocks show and the reverse of
 * TEXT's select-first sheet. Each submit posts a persistent blue bubble in the
 * thread behind the sheet, so the share reads as talking rather than filling a
 * form — the whole reason this variant lands in a thread.
 *
 * THE TWO BUBBLES ARE TWO DIFFERENT MESSAGE TYPES. This is the corrected
 * contract (refine Gaps, 2026-08-15) and the sheet's shape depends on it:
 * `announceAnonName` and `commitAnonContactRecord` both emit a `contact`
 * message under the SAME deterministic id (`contact_${room}_${sender}`), so
 * they upsert one row and the second replaces the first — one row cannot
 * render two persistent bubbles. Instead the NAME rides a `chat` message's
 * `anonName` (the global handler read-modify-writes, so it merge-preserves
 * `sharedFields`) and the VALUE lands as the one authoritative `contact`
 * write. Two bubbles, one contact record, no same-id race.
 *
 * Hence two callbacks, not one: `onNameSubmit` fires the moment the name step
 * completes (which is what puts the bubble behind the selection step, exactly
 * as `anon_5` shows), and `onComplete` fires once at the final submit.
 *
 * Selection rows are all-white here — TEXT leads with a black Phone pill, the
 * THREAD mocks do not. Sheet chrome is the shared QuickAddOverlay /
 * ShareLocationSheet Framer-Motion pattern (ui-design-system §4.2).
 */
const AnonThreadShareSheet = ({
  profileData,
  sharedName = '',
  onNameSubmit,
  onComplete,
  onClose,
}) => {
  // RESUME where they left off. The name is written and its bubble posted the
  // instant the name step completes, so someone who dismissed the sheet before
  // choosing a channel has already shared it — reopening at the name step asked
  // for it a second time and made the first submit look like it hadn't landed
  // (device pass 2026-08-16). Correcting a name is a different door: tapping
  // your own name bubble opens the edit overlay.
  const [step, setStep] = useState(sharedName ? 'select' : 'name');
  const [contactType, setContactType] = useState('phone');
  const [contactValue, setContactValue] = useState('');
  const [name, setName] = useState(sharedName);
  const [expanded, setExpanded] = useState(false);
  const nameInputRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    if (step === 'name') {
      const t = setTimeout(() => nameInputRef.current?.focus(), 150);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [step]);

  // Reciprocity gate — the same predicate the card's channel rows use (a
  // present value is never proof of consent).
  const sharedFields = profileData?.sharedFields || [];
  const optionTypes = [
    ...ALWAYS_TYPES,
    ...IF_SHARED_TYPES.filter((t) => profileData?.[t] && sharedFields.includes(t)),
  ];

  const typeConfig = ALL_CONTACT_TYPES[contactType];
  const trimmedValue = contactValue.trim();
  const trimmedName = name.trim();
  const canSubmit = step === 'value' ? !!trimmedValue : trimmedName.length > 0;

  const handleNameNext = () => {
    if (!trimmedName) return;
    // Fire immediately: the bubble must be in the thread BEFORE the selection
    // step renders over it (anon_5). The parent owns the write.
    onNameSubmit(trimmedName);
    setStep('select');
  };

  const handleSelect = (typeId) => {
    setContactType(typeId);
    setContactValue('');
    setStep('value');
  };

  // No tapPoint here, unlike TEXT's sheet: that value seeds ConfettiBurst's
  // origin, and THREAD has no burst. D22's replacement for it — the centered
  // "Connected with…" card — is gone too (2026-08-20); the confirmation is now
  // the name bubble morphing into a contact card, in the thread itself.
  // The name is already written and bubbled by onNameSubmit, so the completion
  // payload is just the channel and its value.
  const handleValueSubmit = () => {
    if (!trimmedValue) return;
    onComplete({ contactType, contactValue: trimmedValue });
  };

  const title =
    step === 'name'
      ? 'Name'
      : step === 'select'
        ? 'Number or social'
        : contactType === 'phone'
          ? 'Your number'
          : `Your ${typeConfig?.label}`;

  // Two visible, the rest behind a caret — the same collapse the host's
  // ShareSheet uses (COLLAPSED_COUNT = 2), so both sides of the exchange share
  // one gesture and one count.
  const visibleTypes = expanded ? optionTypes : optionTypes.slice(0, COLLAPSED_COUNT);
  const hasMoreTypes = !expanded && optionTypes.length > COLLAPSED_COUNT;

  const renderSelect = () => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
      {visibleTypes.map((typeId) => (
        <button
          key={typeId}
          aria-label={`Share ${ALL_CONTACT_TYPES[typeId].label}`}
          onClick={() => handleSelect(typeId)}
          style={whitePill()}
        >
          {ALL_CONTACT_TYPES[typeId].label}
        </button>
      ))}

      {hasMoreTypes && (
        <button
          aria-label="More options"
          onClick={() => setExpanded(true)}
          style={{
            background: 'none',
            border: 'none',
            padding: '2px 12px',
            cursor: 'pointer',
            lineHeight: 0,
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <CaretDown size={20} color={BUTTON_TOKENS.ACTION_TEXT_SECONDARY} weight="regular" />
        </button>
      )}
    </div>
  );

  return (
    <>
      {/* INVISIBLE backdrop — dismiss without dimming. A dark scrim tints
          Safari's toolbars and reads as a band of grey sandwiched between the
          light browser chrome above and below (ui-design-system §4.8, the same
          conflict that made WelcomeOverlay no-dim). Elevation does the
          separating instead, which is also what the host's ShareSheet does —
          so both sides of the exchange now behave the same.
          A SIBLING of the sheet, not a parent: the sheet's y transform would
          trap a fixed child inside its containing block. */}
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 1999, background: 'transparent' }}
      />

      <motion.div
        initial={{ y: 400 }}
        animate={{ y: 0 }}
        exit={{ y: 400 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2000,
          backgroundColor: 'white',
          borderTopLeftRadius: '24px',
          borderTopRightRadius: '24px',
          // Elevation carries the separation now that nothing dims behind it.
          boxShadow: SHADOWS.TIER3,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px',
            padding: '28px 24px',
            paddingBottom: 'calc(28px + env(safe-area-inset-bottom))',
            maxWidth: '340px',
            margin: '0 auto',
          }}
        >
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#000000' }}>{title}</div>

          {step === 'select' ? (
            renderSelect()
          ) : (
            <div style={{ width: '100%' }}>
              {step === 'value' ? (
                <ContactTypeInput
                  typeConfig={typeConfig}
                  value={contactValue}
                  onChange={setContactValue}
                  onEnter={handleValueSubmit}
                  // Titled sheet — the title names the field, so the
                  // placeholder would just repeat it
                  placeholder=""
                />
              ) : (
                <PillInput
                  ref={nameInputRef}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleNameNext();
                  }}
                  // No placeholder — the sheet's title already says "Name",
                  // and repeating it is noise in an empty field (user, 2026-08-16)
                  placeholder=""
                  autoComplete="name"
                  autoCapitalize="words"
                />
              )}
            </div>
          )}

          {/* The selection step commits on tap, so it carries no button.
              "Connect" rides the final submit, where the connection actually
              happens — the same placement TEXT's device passes settled on. */}
          {step !== 'select' && (
            <ButtonRounded
              text={step === 'name' ? 'Next' : 'Connect'}
              variant="primary"
              size="large"
              disabled={!canSubmit}
              onClick={step === 'name' ? handleNameNext : handleValueSubmit}
            />
          )}
        </div>
      </motion.div>
    </>
  );
};

AnonThreadShareSheet.propTypes = {
  profileData: PropTypes.object,
  sharedName: PropTypes.string,
  onNameSubmit: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AnonThreadShareSheet;
